import type {
  AiPromptQuality,
  AppFilterOption,
  AutoFormatting,
  AutoFormattingCategory,
  CategoryFilterOption,
  DesktopAppRow,
  DesktopAppsResponse,
  DictionaryStats,
  ExportWordsDictatedColumnsEnum,
  FeatureUtilization,
  FilterOptionsResponse,
  ImpactResponse,
  MinutesSaved,
  TeamFilterOption,
  TeamInsightsFilters,
  TeamInsightsFiltersComparisonModeEnum,
  TeamMembersDailyPoint,
  TeamMembersResponse,
  TeamMembersResponseModeEnum,
  UserFilterOption,
  UserFilterOptionsResponse,
  ValueCalculator,
  WordsDictatedDailyPoint,
  WordsDictatedResponse,
  WordsDictatedTimeseries,
  WordsDictatedWeeklyPoint,
} from './generated'
import { fetchAPI } from '.'

export type {
  AiPromptQuality,
  AppFilterOption,
  AutoFormatting,
  AutoFormattingCategory,
  CategoryFilterOption,
  DesktopAppRow,
  DesktopAppsResponse,
  DictionaryStats,
  FeatureUtilization,
  FilterOptionsResponse,
  ImpactResponse,
  MinutesSaved,
  TeamFilterOption,
  TeamInsightsFilters,
  TeamMembersDailyPoint,
  TeamMembersResponse,
  UserFilterOption,
  UserFilterOptionsResponse,
  ValueCalculator,
  WordsDictatedDailyPoint,
  WordsDictatedResponse,
  WordsDictatedTimeseries,
  WordsDictatedWeeklyPoint,
}

export type TeamMembersMode = TeamMembersResponseModeEnum
export type ComparisonMode = TeamInsightsFiltersComparisonModeEnum

// Request-side shape for the Insights endpoints. Mirrors the query
// parameters in the OpenAPI spec for getDesktopApps/getWordsDictated/
// getTeamMembers.
export type TeamInsightsFilterParams = {
  start_date?: string
  end_date?: string
  team_id?: string
  user_ids?: string[]
  app_keys?: string[]
  app_categories?: string[]
  comparison_mode?: ComparisonMode
}

type CleanedParams = Record<string, string | string[]>

const stripEmpty = (params: TeamInsightsFilterParams): CleanedParams => {
  const out: CleanedParams = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      out[k] = v
    } else if (v !== '') {
      out[k] = v
    }
  }
  return out
}

// Repeat array params (?app_keys=A&app_keys=B), no brackets, no comma-join.
const REPEAT_SERIALIZER = { indexes: null as null }

export async function getWordsDictated(enterpriseId: string, filters: TeamInsightsFilterParams = {}) {
  return fetchAPI<WordsDictatedResponse>({
    method: 'GET',
    url: '/insights/words-dictated',
    params: { enterprise_id: enterpriseId, ...stripEmpty(filters) },
    paramsSerializer: REPEAT_SERIALIZER,
  })
}

export async function getTeamMembers(
  enterpriseId: string,
  filters: TeamInsightsFilterParams = {},
  mode: TeamMembersMode = 'weekly'
) {
  return fetchAPI<TeamMembersResponse>({
    method: 'GET',
    url: '/insights/team-members',
    params: { enterprise_id: enterpriseId, ...stripEmpty(filters), mode },
    paramsSerializer: REPEAT_SERIALIZER,
  })
}

export async function getDesktopApps(enterpriseId: string, filters: TeamInsightsFilterParams = {}) {
  return fetchAPI<DesktopAppsResponse>({
    method: 'GET',
    url: '/insights/desktop-apps',
    params: { enterprise_id: enterpriseId, ...stripEmpty(filters) },
    paramsSerializer: REPEAT_SERIALIZER,
  })
}

export async function getImpact(enterpriseId: string, filters: TeamInsightsFilterParams = {}) {
  const { team_id } = filters
  return fetchAPI<ImpactResponse>({
    method: 'GET',
    url: '/insights/impact',
    params: { enterprise_id: enterpriseId, ...stripEmpty({ team_id }) },
  })
}

// Page-load filter dropdowns: teams + apps + categories in one round trip.
// Mirrors the OpenAPI listFilterOptions endpoint. Users are fetched
// separately via getFilterOptionUsers (typeahead, different cache profile).
export async function getFilterOptions(enterpriseId: string) {
  return fetchAPI<FilterOptionsResponse>({
    method: 'GET',
    url: '/insights/filter-options',
    params: { enterprise_id: enterpriseId },
  })
}

export async function getFilterOptionUsers(enterpriseId: string, q?: string, limit = 50, offset = 0) {
  return fetchAPI<UserFilterOptionsResponse>({
    method: 'GET',
    url: '/insights/filter-options/users',
    params: {
      enterprise_id: enterpriseId,
      ...(q ? { q } : {}),
      limit: String(limit),
      offset: String(offset),
    },
  })
}

export type WordsDictatedExportColumn = ExportWordsDictatedColumnsEnum

const EXPORT_TIMEOUT_MS = 60_000

export async function exportWordsDictatedCsv(
  enterpriseId: string,
  filters: TeamInsightsFilterParams,
  columns: WordsDictatedExportColumn[]
) {
  return fetchAPI<Blob>({
    method: 'GET',
    url: '/insights/words-dictated/export',
    params: { enterprise_id: enterpriseId, ...stripEmpty(filters), columns },
    paramsSerializer: REPEAT_SERIALIZER,
    responseType: 'blob',
    timeout: EXPORT_TIMEOUT_MS,
  })
}
